import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { CatalogRequest, PersonRef } from '@/features/catalog/api'
import { onboardingKeys } from '@/features/onboarding/api'
import type { ListMeta } from '@/lib/api'
import { useApi } from '@/lib/api-context'
import { useIdempotencyKey } from '@/lib/use-idempotency-key'

/**
 * Procedimentos da clínica (docs/api/admin.md, "Procedimentos"; RN-ADM-30 a 38 e 70 a 77).
 * O procedimento vem da lista mestre: tem `items` do catálogo e herda os documentos deles.
 */

export type PaymentMethod = 'pix' | 'debit' | 'credit' | 'cash'
export type ProcedureType = 'single' | 'club_or_package'

export interface PaymentTerms {
  methods: PaymentMethod[]
  creditInstallments: number | null
  isInterestFree: boolean | null
  upfrontDiscount: { percent: number; methods: PaymentMethod[] } | null
}

export interface ProcedureItem {
  catalogProcedure: { id: string; name: string; groupName: string; isArchived: boolean }
  quantity: number
}

export interface ProcedureDocument {
  templateId: string
  name: string
  category: string
  isRequired: boolean
  catalogProcedureName: string
}

export interface Procedure {
  id: string
  name: string
  description: string | null
  type: ProcedureType
  items: ProcedureItem[]
  applicationCount: number
  validityMonths: number | null
  amountCents: number
  paymentTerms: PaymentTerms | null
  paymentTermsText: string
  paymentTermsSummary: string
  documents: ProcedureDocument[]
  hasConsentForm: boolean
  isPendingCatalogLink: boolean
  suggestedCatalogProcedure: { id: string; name: string } | null
  archivedAt: string | null
  archivedBy: PersonRef | null
  createdAt: string
  updatedAt: string
}

/** O corpo de criar e editar. O tipo não vai: sai da composição (T47). */
export interface ProcedureInput {
  name?: string
  description: string | null
  items: { catalogProcedureId: string; quantity: number }[]
  validityMonths: number | null
  amountCents: number
  paymentTerms: PaymentTerms
}

export type ProcedureFilter = 'all' | 'single' | 'club_or_package' | 'pending' | 'archived'

export interface ProcedureListMeta extends ListMeta {
  counts: Record<ProcedureFilter, number>
}

export interface ProcedureUsage {
  contractCount: number
  treatmentPlanCount: number
  lastUsedAt: string | null
}

export const procedureKeys = {
  all: ['procedures'] as const,
  list: (params: object) => ['procedures', 'list', params] as const,
  detail: (id: string) => ['procedures', 'detail', id] as const,
  requests: ['procedures', 'catalog-requests'] as const,
}

export function useProcedures(params: {
  filter: ProcedureFilter
  search?: string
  page?: number
  perPage?: number
}) {
  const api = useApi()
  return useQuery({
    queryKey: procedureKeys.list(params),
    queryFn: () =>
      api.list<Procedure, ProcedureListMeta>('/admin/procedures', {
        filter: params.filter,
        search: params.search,
        page: params.page ?? 1,
        perPage: params.perPage ?? 25,
      }),
    placeholderData: (previous) => previous,
  })
}

export function useProcedure(id: string | undefined) {
  const api = useApi()
  return useQuery({
    queryKey: procedureKeys.detail(id ?? ''),
    queryFn: () => api.get<Procedure>(`/admin/procedures/${id}`),
    enabled: Boolean(id),
  })
}

/** Toda escrita em procedimentos muda a lista, a lista mestre (o que já está no catálogo) e a implantação. */
function useInvalidateProcedures() {
  const queryClient = useQueryClient()
  return () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: procedureKeys.all }),
      queryClient.invalidateQueries({ queryKey: ['catalog-procedures', 'options'] }),
      queryClient.invalidateQueries({ queryKey: onboardingKeys.all }),
    ])
}

export function useSaveProcedure(id?: string) {
  const api = useApi()
  const invalidate = useInvalidateProcedures()
  const idempotency = useIdempotencyKey()
  return useMutation({
    mutationFn: (input: ProcedureInput) =>
      id
        ? api.put<Procedure>(`/admin/procedures/${id}`, input)
        : api.post<Procedure>('/admin/procedures', input, {
            idempotencyKey: idempotency.keyFor(input),
          }),
    onSuccess: () => {
      idempotency.reset()
      return invalidate()
    },
  })
}

export function useCreateProcedures() {
  const api = useApi()
  const invalidate = useInvalidateProcedures()
  const idempotency = useIdempotencyKey()
  return useMutation({
    mutationFn: (procedures: ProcedureInput[]) =>
      api.post<{ createdCount: number; procedures: Procedure[] }>(
        '/admin/procedures/bulk',
        { procedures },
        { idempotencyKey: idempotency.keyFor(procedures) },
      ),
    onSuccess: () => {
      idempotency.reset()
      return invalidate()
    },
  })
}

/** O arquivo da importação, lido pelo app: o nome e o texto do CSV (RN-ADM-37). */
export interface ProcedureImportFile {
  fileName: string
  content: string
}

/** Um problema do arquivo: a linha da planilha (o cabeçalho é a 1) e a coluna. */
export interface ProcedureImportError {
  line: number
  /** O cabeçalho da coluna no CSV; nulo quando o problema é da linha inteira. */
  field: string | null
  message: string
}

/** O resultado de `?dryRun=true`: o que o arquivo tem e o que precisa de correção. Nada é gravado. */
export interface ProcedureImportValidation {
  isDryRun: true
  isValid: boolean
  total: number
  byType: Record<ProcedureType, number>
  invalidLineCount: number
  errors: ProcedureImportError[]
  omittedErrorCount: number
}

export interface ProcedureImportCreation {
  isDryRun: false
  isValid: true
  total: number
  byType: Record<ProcedureType, number>
  createdCount: number
}

/** Confere o arquivo sem gravar. Pode repetir à vontade: não cria nada. */
export function useValidateProcedureImport() {
  const api = useApi()
  return useMutation({
    mutationFn: (file: ProcedureImportFile) =>
      api.post<ProcedureImportValidation>('/admin/procedures/import?dryRun=true', file),
  })
}

/** Grava o arquivo inteiro, tudo ou nada. */
export function useImportProcedures() {
  const api = useApi()
  const invalidate = useInvalidateProcedures()
  const idempotency = useIdempotencyKey()
  return useMutation({
    mutationFn: (file: ProcedureImportFile) =>
      api.post<ProcedureImportCreation>('/admin/procedures/import', file, {
        idempotencyKey: idempotency.keyFor(file),
      }),
    onSuccess: () => {
      idempotency.reset()
      return invalidate()
    },
  })
}

export function useProcedureAction() {
  const api = useApi()
  const invalidate = useInvalidateProcedures()
  return useMutation({
    mutationFn: async ({
      id,
      action,
    }: {
      id: string
      action: 'archive' | 'restore' | 'delete'
    }) => {
      if (action === 'delete') return api.delete(`/admin/procedures/${id}`)
      await api.post<Procedure>(`/admin/procedures/${id}/${action}`)
    },
    onSuccess: invalidate,
  })
}

export function useProcedureUsage(id: string | null) {
  const api = useApi()
  return useQuery({
    queryKey: ['procedures', 'usage', id],
    queryFn: () => api.get<ProcedureUsage>(`/admin/procedures/${id}/usage`),
    enabled: Boolean(id),
  })
}

/** Os pedidos de inclusão da clínica na lista mestre (RN-ADM-75). */
export function useOwnCatalogRequests(options: { enabled?: boolean } = {}) {
  const api = useApi()
  return useQuery({
    queryKey: procedureKeys.requests,
    queryFn: () => api.get<CatalogRequest[]>('/admin/procedures/catalog-requests'),
    enabled: options.enabled ?? true,
  })
}

export function useCreateCatalogRequest() {
  const api = useApi()
  const queryClient = useQueryClient()
  const idempotency = useIdempotencyKey()
  return useMutation({
    mutationFn: (input: { name: string; note: string | null }) =>
      api.post<CatalogRequest>('/admin/procedures/catalog-requests', input, {
        idempotencyKey: idempotency.keyFor(input),
      }),
    onSuccess: () => {
      idempotency.reset()
      return queryClient.invalidateQueries({ queryKey: procedureKeys.requests })
    },
  })
}
